/**
 * StatementFilters.js
 * Header component with search, filters, and sorting controls
 */

import React from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  IconButton,
  InputAdornment,
  alpha,
  useTheme,
} from '@mui/material';
import { Search as SearchIcon, Sort as SortIcon } from '@mui/icons-material';

const StatementFilters = ({
  searchTerm,
  filterTab,
  sortOrder,
  stats,
  onSearchChange,
  onFilterTabChange,
  onSortToggle,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
      <Stack spacing={2}>
        {/* Search Bar */}
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar extratos por nome ou conta bancária..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
              },
              '&.Mui-focused': {
                backgroundColor: 'background.paper',
              },
            },
          }}
        />

        {/* Filter Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={filterTab}
            onChange={onFilterTabChange}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                minHeight: 40,
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
              },
            }}
          >
            <Tab
              label={
                <Badge badgeContent={stats.total} color="primary" max={99}>
                  Todos
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={stats.completed} color="success" max={99}>
                  Concluídos
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={stats.inProgress} color="warning" max={99}>
                  Em Andamento
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={stats.pending} color="default" max={99}>
                  Pendentes
                </Badge>
              }
            />
          </Tabs>
        </Box>

        {/* Sort Controls */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {stats.total} extrato{stats.total !== 1 ? 's' : ''} encontrado
            {stats.total !== 1 ? 's' : ''}
          </Typography>
          <Tooltip title={`Ordenar por ${sortOrder === 'newest' ? 'mais antigo' : 'mais recente'}`}>
            <IconButton size="small" onClick={onSortToggle}>
              <SortIcon
                sx={{
                  transform: sortOrder === 'newest' ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

export default StatementFilters;
